import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { SiteListItem } from '../../../types/siteData';
import { useSiteData } from '../../../contexts/SiteDataContext';
import { siteService } from '../../../services/siteService';
import { formatCurrency } from '../../../utils/formatters';
import SiteFormModal from './SiteFormModal';
import './SiteManagement.css';

type SortKey = 'alias' | 'type_name' | 'section_name' | 'effective_rate' | 'status';

interface PrerequisiteStatus {
  hasSiteTypes: boolean;
  hasSections: boolean;
  hasHookupTypes: boolean;
}

const SiteManagementCard: React.FC = () => {
  // Use shared site data context
  const {
    state,
    displayedSites,
    filteredCount,
    loadSites,
    loadMoreSites,
    loadSiteDetails,
    getSiteDetails,
    setFilters,
    setSort,
    clearFilters,
    onSiteUpdated,
    onSiteDeleted,
    refreshSites,
  } = useSiteData();

  const {
    storeConfig,
    storeConfigLoading,
    storeConfigLoaded,
    sitesLoading,
    sitesError,
    sitesLoaded,
    pagination,
    filters,
    sort,
  } = state;

  // Local UI state
  const [isExpanded, setIsExpanded] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState<number | null>(null);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Load sites when store config is ready and card is expanded
  useEffect(() => {
    if (isExpanded && storeConfigLoaded && !sitesLoaded && !sitesLoading) {
      loadSites();
    }
  }, [isExpanded, storeConfigLoaded, sitesLoaded, sitesLoading, loadSites]);

  // Calculate prerequisites
  const prerequisites = useMemo((): PrerequisiteStatus => {
    if (!storeConfig) {
      return { hasSiteTypes: false, hasSections: false, hasHookupTypes: false };
    }
    return {
      hasSiteTypes: storeConfig.siteTypes.filter(t => t.active).length > 0,
      hasSections: storeConfig.sections.filter(s => s.active).length > 0,
      hasHookupTypes: storeConfig.hookupTypes.filter(h => h.active).length > 0,
    };
  }, [storeConfig]);

  const canAddSites = prerequisites.hasSiteTypes;

  // Handle scroll for pagination
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 200;

    if (nearBottom && pagination.hasMore && !sitesLoading) {
      loadMoreSites();
    }
  }, [pagination.hasMore, sitesLoading, loadMoreSites]);

  // Handle sort change
  const handleSort = useCallback((key: SortKey) => {
    setSort(key);
  }, [setSort]);

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: { search?: string; type_id?: number; section_id?: number }) => {
    setFilters(newFilters);
  }, [setFilters]);

  // Modal handlers
  const handleAddSite = useCallback(() => {
    setEditingSiteId(null);
    setShowModal(true);
  }, []);

  const handleEditSite = useCallback(async (siteId: number) => {
    // Pre-load site details before opening modal
    await loadSiteDetails(siteId);
    setEditingSiteId(siteId);
    setShowModal(true);
  }, [loadSiteDetails]);

  const handleDeleteSite = useCallback(async (siteId: number, alias: string) => {
    if (!window.confirm(`Are you sure you want to delete site "${alias}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await siteService.deleteSite(siteId);
      onSiteDeleted(siteId);
    } catch (err) {
      console.error('Error deleting site:', err);
      alert('Failed to delete site. It may have active reservations.');
    }
  }, [onSiteDeleted]);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setEditingSiteId(null);
  }, []);

  const handleModalSave = useCallback(async () => {
    setShowModal(false);
    if (editingSiteId) {
      onSiteUpdated(editingSiteId);
    } else {
      // New site created - refresh the list
      refreshSites();
    }
    setEditingSiteId(null);
  }, [editingSiteId, onSiteUpdated, refreshSites]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refreshSites();
  }, [refreshSites]);

  // Get status badge class
  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'available': return 'status-badge-available';
      case 'occupied': return 'status-badge-occupied';
      case 'maintenance': return 'status-badge-maintenance';
      case 'blocked': return 'status-badge-blocked';
      case 'seasonal': return 'status-badge-seasonal';
      default: return '';
    }
  };

  // Render sort indicator
  const renderSortIndicator = (key: SortKey) => {
    if (sort.key !== key) return <span className="sort-indicator">⇅</span>;
    return (
      <span className="sort-indicator active">
        {sort.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Loading state for initial load
  const isInitialLoading = storeConfigLoading || (sitesLoading && displayedSites.length === 0);

  return (
    <div className="config-card site-management-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">🏕️</span>
          <h2>Sites</h2>
          <span className="site-count-badge">
            {displayedSites.length} of {filteredCount} sites
            {pagination.hasMore && ' (scroll for more)'}
          </span>
        </div>
        <div className="config-card-header-actions">
          <button
            className="btn btn-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleRefresh();
            }}
            disabled={sitesLoading}
            title="Refresh site list"
          >
            {sitesLoading ? '...' : '⟳'}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleAddSite();
            }}
            disabled={!canAddSites}
            title={canAddSites ? 'Add new site' : 'Configure site types first'}
          >
            + Add Site
          </button>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {!canAddSites && storeConfigLoaded && (
            <div className="prerequisites-warning">
              <span className="warning-icon">⚠️</span>
              <span>
                Before adding sites, you must configure at least one{' '}
                {!prerequisites.hasSiteTypes && <strong>Site Type</strong>}
                {!prerequisites.hasSiteTypes && !prerequisites.hasHookupTypes && ' and '}
                {!prerequisites.hasHookupTypes && <strong>Hookup Type</strong>}
                {' '}in the Store Setup section above.
              </span>
            </div>
          )}

          {isInitialLoading ? (
            <div className="config-loading">Loading sites...</div>
          ) : sitesError ? (
            <div className="config-error">{sitesError}</div>
          ) : displayedSites.length === 0 && !sitesLoading ? (
            <div className="config-empty">
              <p>No sites configured yet.</p>
              {canAddSites && (
                <button className="btn btn-primary" onClick={handleAddSite}>
                  + Add Your First Site
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="site-management-filters">
                <input
                  type="text"
                  placeholder="Search by alias..."
                  value={filters.search || ''}
                  onChange={(e) => handleFiltersChange({ ...filters, search: e.target.value })}
                  className="filter-input"
                />
                <select
                  value={filters.type_id || ''}
                  onChange={(e) => handleFiltersChange({ ...filters, type_id: e.target.value ? Number(e.target.value) : undefined })}
                  className="filter-select"
                >
                  <option value="">All Types</option>
                  {storeConfig?.siteTypes.filter(t => t.active).map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <select
                  value={filters.section_id || ''}
                  onChange={(e) => handleFiltersChange({ ...filters, section_id: e.target.value ? Number(e.target.value) : undefined })}
                  className="filter-select"
                >
                  <option value="">All Sections</option>
                  {storeConfig?.sections.filter(s => s.active).map(section => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </select>
                {(filters.search || filters.type_id || filters.section_id) && (
                  <button
                    className="btn btn-text btn-sm"
                    onClick={clearFilters}
                  >
                    Clear
                  </button>
                )}
              </div>

              <div
                className="site-table-scroll-container"
                ref={tableContainerRef}
                onScroll={handleScroll}
                style={{ maxHeight: '400px', overflowY: 'auto' }}
              >
                <table className="config-table site-management-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('alias')}>
                        Alias {renderSortIndicator('alias')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('type_name')}>
                        Type {renderSortIndicator('type_name')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('section_name')}>
                        Section {renderSortIndicator('section_name')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('effective_rate')}>
                        Rate {renderSortIndicator('effective_rate')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('status')}>
                        Status {renderSortIndicator('status')}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedSites.map((site: SiteListItem) => (
                      <tr key={site.id}>
                        <td className="site-alias">{site.alias}</td>
                        <td>{site.type_name || '-'}</td>
                        <td>{site.section_name || '-'}</td>
                        <td className="effective-rate-cell">
                          {site.effectivePricing ? (
                            <div className="effective-rate-display">
                              <span className="rate-amount">
                                {formatCurrency(site.effectivePricing.price)}/n
                              </span>
                              <span className={`source-badge-small ${site.effectivePricing.source}`}>
                                {site.effectivePricing.source}
                              </span>
                              {site.effectivePricing.hasSpecialPricing && (
                                <span className="special-indicator" title={site.effectivePricing.specialPricingDateRange}>
                                  ★
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="no-rate">-</span>
                          )}
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusBadgeClass(site.status)}`}>
                            {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                          </span>
                        </td>
                        <td>
                          <div className="config-actions">
                            <button
                              className="btn btn-text btn-sm"
                              onClick={() => handleEditSite(site.id)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-text btn-sm btn-danger"
                              onClick={() => handleDeleteSite(site.id, site.alias)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {sitesLoading && displayedSites.length > 0 && (
                  <div className="sites-loading-more">
                    <div className="loading-spinner-small"></div>
                    <span>Loading more sites...</span>
                  </div>
                )}

                {!pagination.hasMore && displayedSites.length > 0 && (
                  <div className="sites-end-of-list">
                    All {displayedSites.length} sites loaded
                  </div>
                )}
              </div>

              {displayedSites.length === 0 && filteredCount > 0 && !sitesLoading && (
                <div className="no-results">No sites match your filters.</div>
              )}
            </>
          )}
        </div>
      )}

      {showModal && (
        <SiteFormModal
          siteId={editingSiteId}
          siteTypes={storeConfig?.siteTypes || []}
          sections={storeConfig?.sections || []}
          hookupTypes={storeConfig?.hookupTypes || []}
          storePricing={storeConfig?.storePricing || []}
          storeTaxes={storeConfig?.storeTaxes || []}
          storeCharges={storeConfig?.storeCharges || []}
          storeOverageFees={storeConfig?.storeOverageFees || []}
          onClose={handleModalClose}
          onSave={handleModalSave}
          siteDetails={editingSiteId ? getSiteDetails(editingSiteId) : undefined}
        />
      )}
    </div>
  );
};

export default SiteManagementCard;
